import { ChevronDown, Save, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'src/components/ui/button';
import ManageClientAccountCampaignSelection from './manage-client-account-campaign-selection';
import BorderedMultiSelect from 'src/components/form-new/bordered-multi-select';
import BorderedInput from 'src/components/form-new/bordered-input';
import { useGetAccountsQuery, useGetCampaignsQuery } from '../../endpoints/accounts.endpoints';
import { MANAGE_LIST_FETCH_PAGE_SIZE } from '../../types/client-management.types';
import { useGetCompaniesQuery } from 'src/redux/billing/billing.endpoints';
import { useActiveClient } from 'src/redux/active-client/use-active-client';
import { useUpdateClientAccountAccessMutation, useUpdateClientMutation } from '../../endpoints/client.endpoints';
import { ClientAccess, ClientAccessRequest, ClientView } from '../../types/client.types';
import notificationService from 'src/services/notification/notification.service';
import { useDispatch } from 'react-redux';
import { setActiveClient } from 'src/redux/active-client/active-client.slice';

interface ManageClientAccessProps {
  onSelectionChange: (accountIds: string[], campaignIds: string[]) => void;
  onOpenChange: (open: boolean) => void;
}

interface SelectedItem {
  id: string;
  title: string;
  type: 'account' | 'campaign';
}

interface SelectedCampaign {
  source: string;
  accountId: string; // ext_account_id
  campaignId: string; // ext_campaign_id
}

const ManageClientAccess: React.FC<ManageClientAccessProps> = ({ onOpenChange, onSelectionChange }) => {
  const { activeClient } = useActiveClient();
  const dispatch = useDispatch();

  const [searchValue, setSearchValue] = useState('');
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState<SelectedCampaign[]>([]);
  const [accountFilter, setAccountFilter] = useState<string[]>([]);
  const [companyFilter, setCompanyFilter] = useState<string[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);

  const { data: accountsPage } = useGetAccountsQuery({
    page: 1,
    page_size: MANAGE_LIST_FETCH_PAGE_SIZE,
  });
  const { data: campaignsPage } = useGetCampaignsQuery({
    page: 1,
    page_size: MANAGE_LIST_FETCH_PAGE_SIZE,
  });
  const accounts = accountsPage?.items;
  const campaigns = campaignsPage?.items;
  const { data: companies } = useGetCompaniesQuery();
  const [updateClientAccountAccess] = useUpdateClientAccountAccessMutation();
  const [updateClient] = useUpdateClientMutation();

  // Extract existing access from activeClient.access and convert to SelectedCampaign format
  const existingCampaigns = useMemo<SelectedCampaign[]>(() => {
    if (!activeClient?.access || !accounts || !campaigns) return [];

    return activeClient.access
      .map(access => {
        // Find account by ext_account_id or id
        const account = accounts.find(acc => acc.ext_account_id === access.account || acc.id === access.account);
        // Find campaign by ext_campaign_id or id
        const campaign = campaigns.find(camp => camp.ext_campaign_id === access.campaign || camp.id === access.campaign);

        if (account?.ext_account_id && campaign?.ext_campaign_id) {
          return {
            source: account.source, // Get source from account
            accountId: account.ext_account_id,
            campaignId: campaign.ext_campaign_id,
          };
        }
        return null;
      })
      .filter((item): item is SelectedCampaign => item !== null);
  }, [activeClient?.access, accounts, campaigns]);

  // Extract unique account IDs from existing access
  const existingAccountIds = useMemo(() => {
    if (!activeClient?.access || !accounts) return [];
    const accountIds = activeClient.access
      .map(access => {
        const account = accounts.find(acc => acc.ext_account_id === access.account || acc.id === access.account);
        return account?.ext_account_id;
      })
      .filter(Boolean) as string[];
    return Array.from(new Set(accountIds));
  }, [activeClient?.access, accounts]);

  // Initialize selected IDs and campaigns from existing access
  // Reset when client changes
  useEffect(() => {
    if (activeClient?.ext_client_id && accounts && campaigns) {
      if (activeClient?.access) {
        setSelectedAccountIds(existingAccountIds);
        setSelectedCampaigns(existingCampaigns);
      } else {
        // Reset if no access data yet
        setSelectedAccountIds([]);
        setSelectedCampaigns([]);
      }
    }
  }, [activeClient?.ext_client_id, activeClient?.access, accounts, campaigns, existingAccountIds, existingCampaigns]);

  // Convert selectedCampaigns to campaignIds array for the selection component
  const selectedCampaignIds = useMemo(() => {
    return selectedCampaigns.map(sc => sc.campaignId);
  }, [selectedCampaigns]);

  // Calculate what needs to be added and removed
  const accessChanges = useMemo(() => {
    const toAdd: SelectedCampaign[] = [];
    const toRemove: ClientAccess[] = [];

    // Create a map of existing access for quick lookup
    const existingMap = new Map<string, ClientAccess>();
    activeClient?.access?.forEach(access => {
      const key = `${access.account}:${access.campaign}`;
      existingMap.set(key, access);
    });

    // Create a map of selected campaigns for quick lookup
    const selectedMap = new Map<string, SelectedCampaign>();
    selectedCampaigns.forEach(sc => {
      const key = `${sc.accountId}:${sc.campaignId}`;
      selectedMap.set(key, sc);
    });

    // Find campaigns to add (in selected but not in existing)
    selectedCampaigns.forEach(sc => {
      // Find matching existing access by account and campaign
      const existingAccess = activeClient?.access?.find(access => {
        const account = accounts?.find(acc => acc.ext_account_id === sc.accountId || acc.id === sc.accountId);
        const campaign = campaigns?.find(camp => camp.ext_campaign_id === sc.campaignId || camp.id === sc.campaignId);
        return (
          (account && (account.ext_account_id === access.account || account.id === access.account)) &&
          (campaign && (campaign.ext_campaign_id === access.campaign || campaign.id === access.campaign))
        );
      });

      if (!existingAccess) {
        toAdd.push(sc);
      }
    });

    // Find campaigns to remove (in existing but not in selected)
    activeClient?.access?.forEach(access => {
      const account = accounts?.find(acc => acc.ext_account_id === access.account || acc.id === access.account);
      const campaign = campaigns?.find(camp => camp.ext_campaign_id === access.campaign || camp.id === access.campaign);

      if (account?.ext_account_id && campaign?.ext_campaign_id) {
        const key = `${account.ext_account_id}:${campaign.ext_campaign_id}`;
        if (!selectedMap.has(key)) {
          toRemove.push(access);
        }
      }
    });

    return { toAdd, toRemove };
  }, [selectedCampaigns, activeClient?.access, accounts, campaigns]);

  const createDefaultClientView = (accountId: string, accountName: string, platform: string): ClientView => ({
    account_ext_id: accountId,
    account_name: accountName,
    platform: platform,
    show_difference: false,
    metrics: [],
    graph_one: {
      metric_one: null,
      metric_two: null,
    },
    graph_two: {
      metric_one: null,
      metric_two: null,
    },
    graph_three: {
      metric_one: null,
      metric_two: null,
    },
  });

  const handleSave = async() => {
    if (!activeClient?.ext_client_id) return;

    try {
      let updatedClient = {
        ...activeClient,
        views: [...activeClient.views],
      };
      accessChanges.toAdd.forEach(async sc => {
        if (!(sc.accountId in updatedClient.views.map(view => view.account_ext_id))) {
          const accountName =accounts?.find(acc => acc.ext_account_id === sc.accountId)?.title;
          const newView = createDefaultClientView(sc.accountId, accountName ?? '', sc.source);
          const updatedViews = [...updatedClient.views, newView];
          updatedClient = {
            ...updatedClient,
            views: updatedViews,
          };
          await updateClient({
            clientId: activeClient.ext_client_id,
            data: updatedClient,
          });
          setActiveClient(updatedClient);
        }
      });

      // Group ALL selected campaigns by account and source
      // This ensures we send all active access, not just changes
      const campaignsByAccountSource = new Map<string, string[]>();
      selectedCampaigns.forEach(sc => {
        const key = `${sc.accountId}:${sc.source}`;
        if (!campaignsByAccountSource.has(key)) {
          campaignsByAccountSource.set(key, []);
        }
        campaignsByAccountSource.get(key)!.push(sc.campaignId);
      });

      // Create access requests for each account+source combination
      const accessRequests: ClientAccessRequest[] = Array.from(campaignsByAccountSource.entries()).map(([key, campaignIds]) => {
        const [accountId, source] = key.split(':');
        return {
          ext_client_id: activeClient.ext_client_id,
          source: source,
          account: accountId,
          campaigns: campaignIds,
        };
      });

      // Send all access requests in one call (backend will handle deletion and recreation)
      await updateClientAccountAccess(accessRequests).unwrap();

      onOpenChange(false);
      notificationService.handleSuccess(null, dispatch, 'Client access saved successfully');
    } catch (error) {
      console.error('Error saving access:', error);
      notificationService.handleError(error, dispatch, 'Error saving client access');
    }
  };

  const getSelectedAccountItems = useMemo(() => {
    return selectedAccountIds.map(id => {
      const account = accounts?.find(acc => acc.ext_account_id === id);
      return {
        id,
        title: account?.title || '',
        type: 'account' as const,
      };
    });
  }, [selectedAccountIds, accounts]);

  const getSelectedCampaignItems = useMemo(() => {
    return selectedCampaigns.map(sc => {
      const campaign = campaigns?.find(camp => camp.ext_campaign_id === sc.campaignId);
      return {
        id: sc.campaignId,
        title: campaign?.title || '',
        type: 'campaign' as const,
      };
    });
  }, [selectedCampaigns, campaigns]);

  // Combine accounts and campaigns into a single array
  const selectedItems = useMemo<SelectedItem[]>(() => {
    return [...getSelectedAccountItems, ...getSelectedCampaignItems];
  }, [getSelectedAccountItems, getSelectedCampaignItems]);

  // Estimate items per row (roughly 8-10 items depending on text length)
  // For 2 rows, we'll show approximately 16-20 items
  const itemsPerRow = 4;
  const maxVisibleItems = itemsPerRow * 2; // 2 rows
  const hasMoreItems = selectedItems.length > maxVisibleItems;
  const visibleItems = isExpanded ? selectedItems : selectedItems.slice(0, maxVisibleItems);

  // Handle selection change from the tree component
  const handleSelectionChange = (accountIds: string[], campaignIds: string[]) => {
    setSelectedAccountIds(accountIds);

    // Convert campaignIds to SelectedCampaign format
    // We need to find the account and source for each campaign
    const newSelectedCampaigns: SelectedCampaign[] = [];
    campaignIds.forEach(campaignId => {
      const campaign = campaigns?.find(camp => camp.ext_campaign_id === campaignId);
      if (campaign) {
        // campaign.account is the Account object, use its ext_account_id
        if (campaign.account?.ext_account_id) {
          newSelectedCampaigns.push({
            source: campaign.account.source,
            accountId: campaign.account.ext_account_id,
            campaignId: campaign.ext_campaign_id,
          });
        }
      }
    });

    setSelectedCampaigns(newSelectedCampaigns);
    onSelectionChange(accountIds, campaignIds);
  };

  return (
    <div>
      <div className="flex-1 overflow-y-auto p-6 pt-3">
        <div className="mt-6">
          <div className="text-sm text-gray-500 mb-2 flex flex-row gap-2 items-center justify-between ">
            <BorderedInput
              label='Search campaigns...'
              placeholder="Search campaigns..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="w-full"
            />
            <div className="flex flex-row gap-2">
              <BorderedMultiSelect
                searchEnabled={true}
                label='Account Filter'
                options={accounts?.map((account) => ({ value: account.id, label: account.title })) || []}
                value={accountFilter}
                onChange={(value) => setAccountFilter(value)}
                className="w-full"
                textClassName="text-sm"
              />
              <BorderedMultiSelect
                searchEnabled={true}
                label='Company Filter'
                options={companies?.map((company) => ({ value: company.id, label: company.company_name })) || []}
                value={companyFilter}
                onChange={(value) => setCompanyFilter(value)}
                className="w-full"
                textClassName="text-sm"
              />
            </div>
          </div>
          <ManageClientAccountCampaignSelection
            accounts={accounts || []}
            campaigns={campaigns || []}
            companies={companies || []}
            selectedCompanies={[]}
            selectedAccountIds={selectedAccountIds}
            selectedCampaignIds={selectedCampaignIds}
            showCompanyGrouping={true}
            searchValue={searchValue}
            accountFilter={accountFilter}
            companyFilter={companyFilter}
            onSelectionChange={handleSelectionChange}
          />
        </div>
      </div>

      {/* Sticky footer with Save and Close buttons */}
      <div
        className={`sticky bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-md z-10 transition-all duration-300 ${
          isExpanded ? 'max-h-[500px]' : 'max-h-[200px]'
        } flex flex-col`}
      >
        {/* Selected items section */}
        <div className={`flex-1 overflow-y-auto p-4 pb-1 ${isExpanded ? 'h-fit' : ''}`}>
          <div className='flex flex-row items-center gap-0 flex-wrap'>
            {visibleItems.length === 0 && (
              <span className='text-sm text-gray-500 px-2 py-0.5 mr-1 mb-1'>
                No items selected
              </span>
            )}
            {visibleItems.map((item) => (
              <span
                className={`text-xs text-gray-500 rounded-full px-2 pr-1 py-0.5 mr-1 mb-1 flex-shrink-0 ${
                  item.type === 'account' ? 'bg-gray-201' : 'bg-gray-200'
                }`}
                key={`${item.type}-${item.id}`}
              >
                {item.title}
              </span>
            ))}
            {!isExpanded && hasMoreItems && (
              <>
                <span className='text-xs text-gray-400 px-2 py-0.5 mr-1 mb-1'>
                  ...
                </span>
                <Button
                  variant='ghost'
                  size='sm'
                  className='text-xs text-blue-500 hover:text-blue-600 px-2 w-full py-0.5 h-auto'
                  onClick={() => setIsExpanded(true)}
                >
                  View All
                  {' '}
                  (
                  {selectedItems.length - maxVisibleItems}
                  {' '}
                  more)
                </Button>
              </>
            )}
            {isExpanded && hasMoreItems && (
              <Button
                variant='ghost'
                size='sm'
                className='text-xs text-blue-500 hover:text-blue-600 w-full py-0.5 h-auto mb-1'
                onClick={() => setIsExpanded(false)}
              >
                <ChevronDown className='w-3 h-3' />
                Show Less
              </Button>
            )}
          </div>
        </div>

        {/* Action buttons section */}
        <div className='flex flex-row gap-2 justify-end p-4 border-t border-gray-200 bg-white'>
          <Button
            variant='ghost'
            size='sm'
            className='bg-gray-201 text-gray-500 hover:bg-gray-201/60 px-3'
            onClick={() => {
              setSearchValue('');
              setAccountFilter([]);
              setCompanyFilter([]);
              setSelectedAccountIds([]);
              setSelectedCampaigns([]);
              onOpenChange(false);
            }}
          >
            <X className='h-4 w-4' />
            Cancel
          </Button>
          <Button
            variant='ghost'
            size='sm'
            disabled={accessChanges.toAdd.length === 0 && accessChanges.toRemove.length === 0}
            className='bg-green-501 text-white hover:bg-green-501/80 px-3 pl-4'
            onClick={handleSave}
          >
            <Save className='h-4 w-4' />
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ManageClientAccess;
